import React, { useEffect } from 'react';
import { CardWrapper } from '@monorepo/common';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { ReceiptLink } from './components/ReceiptLink';
import { useLazyGetReceiptDataQuery } from '../../services/servicePostPrepaidListing';
import { book } from '../../routers/books';
import { useAppSelector } from '../../store/hooks';
import { IPaymentResponse } from '../../services/servicePostAJob/responseInterfaces';
import {
  getEditJobPath,
  getOriginJobSite,
  getPublicJobUrl,
} from '../../utils/jobAccountRoutes';
import { formatOrderAmount } from '../../utils/order';

export const Receipt = () => {
  const [getReceiptData, { data, error, isLoading }] = useLazyGetReceiptDataQuery();
  const { search, state } = useLocation<{ order?: IPaymentResponse }>();
  const { siteId } = useAppSelector(({ pathnameSettings }) => ({
    siteId: pathnameSettings.siteNumber,
  }));
  const query = new URLSearchParams(search);
  const eguid = query.get('eguid');
  const order = state?.order;

  useEffect(() => {
    if (eguid && !order) {
      getReceiptData({
        eguid
      });
    }
  }, [eguid, getReceiptData, order]);

  const receipt = order || data;
  const listingSiteId = receipt?.siteId === undefined
    ? siteId
    : receipt.siteId;
  const publicJobUrl = getPublicJobUrl(listingSiteId, receipt?.path);
  const originJobSite = getOriginJobSite(listingSiteId, receipt?.path);
  const amount = formatOrderAmount(order?.orderAmount);

  return (
    <CardWrapper
      dataCy="final_step_for_page_postJobListing"
      isLoading={isLoading}
    >
      <Typography
        variant="h4"
        marginBottom={4}
      >
        Order Complete - Thank You
      </Typography>

      {!eguid ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          This receipt link is incomplete. Return to your job listings to continue.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          We could not load this receipt. You can still manage the listing from your dashboard.
        </Alert>
      ) : null}

      <Typography
        variant="h5"
        marginBottom={3}
      >
        Your Job Listing
      </Typography>

      <Typography
        variant="body1"
        marginBottom={3}
      >
        Your job listing is now active.
      </Typography>

      {order?.orderId ? (
        <Typography variant="body1" marginBottom={1}>
          <strong>Order ID:</strong>
          {' '}
          {order.orderId}
        </Typography>
      ) : null}

      {amount ? (
        <Typography variant="body1" marginBottom={3}>
          <strong>Amount:</strong>
          {' '}
          {amount}
        </Typography>
      ) : null}

      {publicJobUrl ? (
        <ReceiptLink
          textLink={publicJobUrl}
          link={publicJobUrl}
          title="VIEW YOUR JOB LISTING"
        />
      ) : null}

      {originJobSite ? (
        <ReceiptLink
          textLink={originJobSite.url}
          link={originJobSite.url}
          title={`ALSO VIEW ON ${originJobSite.name.toUpperCase()}`}
        />
      ) : null}

      {eguid ? (
        <ReceiptLink
          isLocal
          textLink="Edit your job listing"
          link={getEditJobPath(listingSiteId, eguid)}
          title="EDIT YOUR JOB LISTING"
        />
      ) : null}

      {order?.orderGuid ? (
        <ReceiptLink
          textLink={`https://app.bevnet.com/orders/receipt/${order.orderGuid}`}
          link={`https://app.bevnet.com/orders/receipt/${order.orderGuid}`}
          title="VIEW ORDER"
        />
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3 }}>
        <Button component={Link} to={book.jobsListings} variant="contained">
          Manage Job Listings
        </Button>
        <Button component={Link} to={book.jobsPrepaid} variant="outlined">
          View Prepaid Packages
        </Button>
      </Stack>

      <Typography
        variant="subtitle1"
        color="#666"
        marginTop={5}
      >
        <b>
          IMPORTANT:
        </b>
        {' '}
        These links -- and a receipt for your purchase -- have also been sent to you via email.
      </Typography>
    </CardWrapper>
  );
};
